import axios from "axios";
import moment from "moment-timezone";
import Form, { Field as BaseField } from "rc-field-form";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Col, Row } from "reactstrap";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import Button from "../../../../../components/Button";
import Field from "../../../../../components/Field";
import RangeDateInput from "../../../../../components/Inputs/DateInput/Range";
import ResourceSelect from "../../../../../components/Inputs/ResourceSelect";
import Select from "../../../../../components/Inputs/Select";
import Loading from "../../../../../components/Loaders/Loading";
import Modal from "../../../../../components/Modal";
import useApi from "../../../../../utils/api";
import { getSettingsResourceSearchFilters, renderSettingsResourceLabel } from "../../../../../utils/helpers/settings";
import { renderUserName } from "../../../../../utils/helpers/user";
import { useCompanyDateFormat } from "../../../../../utils/hooks/company";
import { getUserSearchFilters } from "../../../../Profile/Personal/Actions/ChangeManagement";

function RecalculateTimeSheetModal({ data, visible, close, onSuccess, onFailure }) {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();

    const [form] = Form.useForm();

    const { authPost } = useApi();
    const mutation = useSWRMutation(
        "/time-sheet/users/recalculate",
        (url, { arg }) => {
            const data = {
                ...arg,
                from: arg.range?.[0],
                to: arg.range?.[1],
                users: arg.users?.map((user) => user.id),
                payGroups: arg.payGroups?.map((payGroup) => payGroup.id),
                calculationGroups: arg.calculationGroups?.map((calculationGroup) => calculationGroup.id),
                payGroup: arg.payGroup?.id,
                payGroupItem: arg.payGroupItem?.id,
            };
            delete data.range;
            return authPost(url, { data });
        },
        {
            onSuccess: () => {
                toast.success(t("timesheets-recalculated-successfully"));
                onSuccess?.();
                close();
            },
            onError: (error) => {
                toast.error(t(error || 'could-not-recalculate-timesheets'));
                onFailure?.(error);
            },
            throwOnError: false,
        },
    );

    const { authGet } = useApi();
    const initialManualUsersQuery = useSWR(
        () => {
            if (!data.users?.length) {
                return null;
            }
            return axios.getUri({
                url: "/users",
                params: {
                    id: data.users.map((user) => user.id),
                },
            });
        },
        (url) => {
            return authGet(url);
        },
    );
    const initialPayGroupsQuery = useSWR(
        () => {
            if (data.mode !== "pay-group") {
                return null;
            }
            if (data.users?.length) {
                return null;
            }
            if (!data.payGroup?.id) {
                return null;
            }
            return axios.getUri({
                url: `/pay-group/${data.payGroup.id}`,
            });
        },
        (url) => {
            return authGet(url);
        },
    );

    useEffect(() => {
        let manageBy, users, payGroups, payGroup, dateRangeType, range;
        if (data.mode === "pay-group") {
            dateRangeType = "by-pay-period";
            manageBy = "by-pay-group";
            payGroups = initialPayGroupsQuery.data ? [initialPayGroupsQuery.data] : [];
            payGroup = payGroups[0] || null;
        } else {
            dateRangeType = "manually";
            range = [
                moment(data.startDate).format("YYYY-MM-DD"),
                moment(data.endDate).format("YYYY-MM-DD"),
            ];
        }

        if (data.users?.length) {
            manageBy = "manually";
            users = initialManualUsersQuery.data?.result || [];
        }

        form.setFieldsValue({
            manageBy,
            users,
            payGroups,
            payGroup,
            dateRangeType,
            range,
        });
    }, [data, initialManualUsersQuery.data, initialPayGroupsQuery.data]);

    const isLoading = initialManualUsersQuery.isLoading || initialPayGroupsQuery.isLoading;

    return (
        <Modal
            className="payroll-export-modal"
            size="lg"
            title={
                <div className="d-flex justify-content-between align-items-center text-primary">
                    {t("recalculate-timesheets")}
                    <i
                        className="fa-solid fa-close cursor-pointer"
                        onClick={close}
                    />
                </div>
            }
            toggle={close}
            isOpen={visible}
            scrollable
            centered
        >
            {isLoading ? (
                <div className="d-flex justify-content-center align-items-center py-4">
                    <Loading />
                </div>
            ) : (
                <Form form={form} onFinish={mutation.trigger}>
                    <Row>
                        <BaseField shouldUpdate>
                            {({ }, { }, { getFieldValue }) => {
                                const manageBy = getFieldValue("manageBy");
                                return (
                                    <Col md={manageBy ? 6 : 12}>
                                        <Field
                                            name="manageBy"
                                            label={t("manage-by")}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: t("manage-by-required"),
                                                }
                                            ]}
                                        >
                                            <Select
                                                onChange={(value) => {
                                                    const data = {
                                                        users: [],
                                                        payGroups: [],
                                                        calculationGroups: [],
                                                    };
                                                    if (value === "by-pay-group") {
                                                        delete data.payGroups;
                                                        data.payGroup = null;
                                                    }
                                                    if (value === "by-calculation-group") {
                                                        delete data.calculationGroups;
                                                    }
                                                    if (value === "manually") {
                                                        data.payGroup = null;
                                                    }
                                                    form.setFieldsValue(data);
                                                }}
                                            >
                                                {["manually", "by-pay-group", "by-calculation-group"].map((value) => {
                                                    return (
                                                        <Select.Option key={value} value={value}>
                                                            {t(value)}
                                                        </Select.Option>
                                                    );
                                                })}
                                            </Select>
                                        </Field>
                                    </Col>
                                );
                            }}
                        </BaseField>

                        <Col md={6}>
                            <BaseField shouldUpdate>
                                {({ }, { }, { getFieldValue }) => {
                                    const manageBy = getFieldValue("manageBy");
                                    switch (manageBy) {
                                        case "manually": {
                                            return (
                                                <Field
                                                    name="users"
                                                    label={t("users")}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: t("required-users"),
                                                        }
                                                    ]}
                                                >
                                                    <ResourceSelect
                                                        resourcePath="/users"
                                                        placeholder={t("users")}
                                                        renderLabel={renderUserName}
                                                        hasSearch
                                                        getSearchFilters={getUserSearchFilters}
                                                        mode="multiple"
                                                        onChange={(value) => {
                                                            const dateRangeType = getFieldValue("dateRangeType");
                                                            if (dateRangeType !== "by-pay-period") {
                                                                return;
                                                            }
                                                            const payGroup = getFieldValue("payGroup");
                                                            const payGroupIsInUsers = value.find((item) => item.payGroup === payGroup?.id);
                                                            if (!payGroupIsInUsers) {
                                                                form.setFieldsValue({
                                                                    payGroup: null,
                                                                });
                                                            }
                                                        }}
                                                    />
                                                </Field>
                                            );
                                        }
                                        case "by-pay-group": {
                                            return (
                                                <Field
                                                    name="payGroups"
                                                    label={t("pay-groups")}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: t("required-pay-groups"),
                                                        }
                                                    ]}
                                                >
                                                    <ResourceSelect
                                                        resourcePath="/pay-group"
                                                        placeholder={t("pay-groups")}
                                                        renderLabel={renderSettingsResourceLabel}
                                                        hasSearch
                                                        getSearchFilters={getSettingsResourceSearchFilters}
                                                        mode="multiple"
                                                        onChange={(value) => {
                                                            const dateRangeType = getFieldValue("dateRangeType");
                                                            if (dateRangeType !== "by-pay-period") {
                                                                return;
                                                            }
                                                            if (value.length < 2) {
                                                                form.setFieldsValue({
                                                                    payGroup: value[0] || null,
                                                                });
                                                            } else {
                                                                form.setFieldsValue({
                                                                    dateRangeType: "manually",
                                                                    payGroup: null,
                                                                });
                                                            }
                                                        }}
                                                    />
                                                </Field>
                                            );
                                        }
                                        case "by-calculation-group": {
                                            return (
                                                <Field
                                                    name="calculationGroups"
                                                    label={t("calculation-groups")}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: t("required-calculation-groups"),
                                                        }
                                                    ]}
                                                >
                                                    <ResourceSelect
                                                        resourcePath="/calculation-group"
                                                        placeholder={t("calculation-groups")}
                                                        renderLabel={renderSettingsResourceLabel}
                                                        hasSearch
                                                        getSearchFilters={getSettingsResourceSearchFilters}
                                                        mode="multiple"
                                                    />
                                                </Field>
                                            );
                                        }
                                        default: {
                                            return null;
                                        }
                                    }
                                }}
                            </BaseField>
                        </Col>
                    </Row>

                    <BaseField shouldUpdate dependencies={["manageBy"]}>
                        {({ value }, { }, { getFieldValue, setFieldsValue }) => {
                            const { manageBy, payGroups, dateRangeType } = value;
                            const dateRangeTypeColSize = dateRangeType === "by-pay-period"
                                ? 4
                                : dateRangeType === "manually" ? 6 : 12;
                            return (
                                <Row>
                                    <Col md={dateRangeTypeColSize}>
                                        <Field
                                            name="dateRangeType"
                                            label={t("date-range-type")}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: t("required-date-range-type"),
                                                }
                                            ]}
                                        >
                                            <Select
                                                onChange={(value) => {
                                                    const data = {
                                                        startDate: null,
                                                        endDate: null,
                                                        payGroup: null,
                                                        payGroupItem: null,
                                                    };
                                                    if (
                                                        value === "by-pay-period"
                                                        && manageBy === "by-pay-group"
                                                        && payGroups?.length === 1
                                                    ) {
                                                        data.payGroup = payGroups[0];
                                                    }
                                                    setFieldsValue(data);
                                                }}
                                            >
                                                <Select.Option key="manually" value="manually">
                                                    {t("manually")}
                                                </Select.Option>

                                                <Select.Option
                                                    key="by-pay-period"
                                                    value="by-pay-period"
                                                    disabled={manageBy === 'by-pay-group' && payGroups?.length !== 1}
                                                >
                                                    {t("by-pay-period")}
                                                </Select.Option>

                                                <Select.Option key="by-pay-group-and-current-pay-period" value="by-pay-group-and-current-pay-period">
                                                    {t("by-pay-group-and-current-pay-period")}
                                                </Select.Option>

                                                <Select.Option key="by-pay-group-and-past-pay-period" value="by-pay-group-and-past-pay-period">
                                                    {t("by-pay-group-and-past-pay-period")}
                                                </Select.Option>
                                            </Select>
                                        </Field>
                                    </Col>

                                    {dateRangeType === "manually" && (
                                        <Col md={6}>
                                            {/**
                                             * 
                                             * field is used for the label
                                             * do not add value property on the RangeDateInput
                                             * 
                                             *  */}
                                            <Field
                                                label={t("range")}
                                                name="range"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: t("required-range"),
                                                    },
                                                    {
                                                        validator: ({}, value) => {
                                                            // if value is not set, do not continue with the validation
                                                            if (!value) {
                                                                return Promise.resolve();
                                                            }
                                                            const [start, end] = value;
                                                            if (!start || !end) {
                                                                return Promise.reject(new Error(t("required-range")));
                                                            }
                                                            return Promise.resolve();
                                                        }
                                                    }
                                                ]}
                                                dependencies={["from", "to"]}
                                            >
                                                {({ value, onChange }) => {
                                                    return (
                                                        <RangeDateInput
                                                            startDate={value?.[0] ? moment(value[0]).toDate() : undefined}
                                                            endDate={value?.[1] ? moment(value[1]).toDate() : undefined}
                                                            onChange={([startDate, endDate]) => {
                                                                const data = [
                                                                    moment(startDate).format("YYYY-MM-DD"),
                                                                    moment(endDate).format("YYYY-MM-DD"),
                                                                ];
                                                                onChange(data);
                                                            }}
                                                        />
                                                    );
                                                }}
                                            </Field>

                                            <BaseField hidden name="startDate" />
                                            <BaseField hidden name="endDate" />
                                        </Col>
                                    )}

                                    {dateRangeType === "by-pay-period" && (
                                        <>
                                            <Col md={4}>
                                                {manageBy === 'manually' ? (
                                                    <Field
                                                        name="payGroup"
                                                        label={t("pay-group")}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: t("required-pay-group"),
                                                            }
                                                        ]}
                                                    >
                                                        {({ value, onChange }) => {
                                                            const users = getFieldValue("users");
                                                            const options = Array.from(new Set(users.map((user) => user.payGroup)));
                                                            const resourcePath = axios.getUri({
                                                                url: "/pay-group",
                                                                params: { id: options },
                                                            });
                                                            return (
                                                                <ResourceSelect
                                                                    value={value}
                                                                    onChange={onChange}
                                                                    resourcePath={resourcePath}
                                                                    placeholder={t("pay-group")}
                                                                    renderLabel={renderSettingsResourceLabel}
                                                                />
                                                            );
                                                        }}
                                                    </Field>
                                                ) : (
                                                    <Field
                                                        name="payGroup"
                                                        label={t("pay-group")}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: t("required-pay-group"),
                                                            }
                                                        ]}
                                                    >
                                                        <ResourceSelect
                                                            resourcePath="/pay-group"
                                                            placeholder={t("pay-group")}
                                                            renderLabel={renderSettingsResourceLabel}
                                                            hasSearch
                                                            getSearchFilters={getSettingsResourceSearchFilters}
                                                            disabled={manageBy === "by-pay-group"}
                                                        />
                                                    </Field>
                                                )}
                                            </Col>

                                            <Col md={4}>
                                                <BaseField shouldUpdate>
                                                    {({ }, { }, { getFieldValue }) => {
                                                        const payGroup = getFieldValue("payGroup");
                                                        if (!payGroup) {
                                                            return (
                                                                <Field
                                                                    name="payGroupItem"
                                                                    label={t("pay-period")}
                                                                    rules={[
                                                                        {
                                                                            required: true,
                                                                            message: t("required-pay-period"),
                                                                        }
                                                                    ]}
                                                                >
                                                                    <Select disabled />
                                                                </Field>
                                                            );
                                                        }
                                                        return (
                                                            <Field
                                                                name="payGroupItem"
                                                                label={t("pay-period")}
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: t("required-pay-period"),
                                                                    }
                                                                ]}
                                                            >
                                                                <ResourceSelect
                                                                    resourcePath={`/pay-group/${payGroup?.id}/items?orderBy[periodStartDate]=desc`}
                                                                    placeholder={t("pay-period")}
                                                                    renderLabel={(item) => {
                                                                        return [
                                                                            moment.parseZone(item.periodStartDate).format(dateFormat),
                                                                            moment.parseZone(item.periodEndDate).format(dateFormat),
                                                                        ].join(" - ");
                                                                    }}
                                                                />
                                                            </Field>
                                                        );
                                                    }}
                                                </BaseField>
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            );
                        }}
                    </BaseField>

                    <Row>
                        <Col md={6}>
                            <Button
                                className="w-100"
                                color="secondary"
                                onClick={close}
                                disabled={mutation.isMutating}
                            >
                                {t("cancel")}
                            </Button>
                        </Col>
                        <Col md={6}>
                            <Button
                                className="w-100"
                                color="primary"
                                htmlType="submit"
                                disabled={mutation.isMutating}
                                loading={mutation.isMutating}
                            >
                                {t("recalculate")}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            )}
        </Modal>
    );
}

export default RecalculateTimeSheetModal;
